import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import searchSvg from "../../../img/search.svg";

import { getStoresPaginatedApi, StorePublic } from "../../../views/store/storeApi";
import "./PickStoreModal.css";

type Props = {
  isOpen: boolean;
  token: string;
  onClose: () => void;
  onPicked: (store: StorePublic | null) => void;
  initialStoreId?: string | null;
};

const STORES_LIMIT = 10;

const PickStoreModal: React.FC<Props> = ({ isOpen, token, onClose, onPicked, initialStoreId }) => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<StorePublic[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(initialStoreId ?? null);
  const [storeSearchInput, setStoreSearchInput] = useState<string>("");
  const [storeSearch, setStoreSearch] = useState<string>("");

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setSelectedId(initialStoreId ?? null);
    setStoreSearchInput("");
    setStoreSearch("");
  }, [isOpen, initialStoreId]);

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      try {
        setLoading(true);
        const nome = storeSearch.trim();
        const data = await getStoresPaginatedApi(token, { page, limit: STORES_LIMIT, ...(nome ? { nome } : {}) });
        setItems(data.items ?? []);
        setTotal(data.total ?? 0);
      } catch (e) {
        toast.error("Erro ao carregar lojas.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, token, page, limit, storeSearch]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!loading) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => e.stopPropagation();

  const applyStoreSearch = () => {
    setPage(1);
    setStoreSearch(storeSearchInput);
  };

  const confirmPick = () => {
    const store = items.find((s) => s.id === selectedId) ?? null;
    onPicked(store);
    onClose();
  };

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">Filtrar por Loja (prioridade)</h2>

        <div className="ModalBody">
          <div className="Box">
            <div className="BoxTitle">Selecione uma loja</div>

            <div className="FreelancerSearchRow">
              <input
                className="SlotInputSearch"
                type="text"
                value={storeSearchInput}
                onChange={(e) => setStoreSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyStoreSearch();
                  }
                }}
                disabled={loading}
              />
              <button
                type="button"
                className="FreelancerSearchButton"
                onClick={applyStoreSearch}
                disabled={loading}
              >
                <img src={searchSvg} alt="Buscar" className="FreelancerSearchIcon" />
              </button>
            </div>

            {loading ? (
              <div className="BoxEmpty">Carregando...</div>
            ) : items.length === 0 ? (
              <div className="BoxEmpty">
                {storeSearch.trim()
                  ? "Nenhuma loja encontrada para essa busca."
                  : "Nenhuma loja encontrada."}
              </div>
            ) : (
              <div className="List">
                {items.map((s) => {
                  const checked = selectedId === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`ListItem CheckItem ${checked ? "checked" : ""}`}
                      onClick={() => setSelectedId(s.id)}
                      disabled={loading}
                    >
                      <div className="CheckLeft">
                        <input type="checkbox" checked={checked} readOnly className="Checkbox" />
                      </div>

                      <div className="CheckRight">
                        <div className="ListItemTitle">{s.nomeFantasia}</div>
                        <div className="ListItemSub">
                          {s.endereco ? s.endereco : "Endereço não informado"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="Pager" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="PagerButton PagerButtonLight"
              disabled={loading || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Ant.
            </button>
            <div className="PagerInfo">
              {page} de {totalPages}
            </div>
            <button
              type="button"
              className="PagerButton"
              disabled={loading || page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Próx.
            </button>
          </div>
        </div>

        <div className="ModalActions">
          <button
            type="button"
            className="ModalButton ModalButtonCancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="ModalButton ModalButtonCancel"
            onClick={() => {
              onPicked(null); // limpar
              onClose();
            }}
            disabled={loading}
          >
            Limpar filtro
          </button>

          <button
            type="button"
            className="ModalButton ModalButtonConfirm"
            onClick={confirmPick}
            disabled={loading}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PickStoreModal;
